import { Navigate, Route, Routes, useLocation } from 'react-router'

import { AppShell } from './app/AppShell'
import { useSession } from './auth/useSession'
import { ErrorState, LoadingState } from './components/States'
import { AuditLogsPage } from './pages/AuditLogsPage'
import { ClientDetailPage } from './pages/ClientDetailPage'
import { ClientsPage } from './pages/ClientsPage'
import { CredentialsPage } from './pages/CredentialsPage'
import { DashboardPage } from './pages/DashboardPage'
import { InboundRejectionsPage } from './pages/InboundRejectionsPage'
import { LoginPage } from './pages/LoginPage'
import { MerchantsPage } from './pages/MerchantsPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { TransactionDetailPage } from './pages/TransactionDetailPage'
import { TransactionsPage } from './pages/TransactionsPage'
import { UsersPage } from './pages/UsersPage'
import { SecurityPage } from './pages/SecurityPage'
import { WebhooksPage } from './pages/WebhooksPage'

/**
 * Rute konsol. Setiap butir menu punya halaman nyata; rute yang tidak dikenal
 * berakhir di halaman 404 dengan jalan kembali ke dashboard.
 */
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedShell />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/transactions/:id" element={<TransactionDetailPage />} />
        <Route path="/webhooks" element={<WebhooksPage />} />
        <Route path="/inbound-rejections" element={<InboundRejectionsPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/merchants" element={<MerchantsPage />} />
        <Route path="/credentials" element={<CredentialsPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/users" element={<AdminOnly element={<UsersPage />} />} />
        <Route path="/audit-logs" element={<AuditLogsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}

/**
 * Menahan halaman sampai sesi diketahui. Selama permintaan /auth/me berjalan,
 * konsol menampilkan apa yang sedang dimuat; bila server tidak bisa dihubungi,
 * operator diberi penjelasan dan tombol coba lagi alih-alih halaman kosong.
 */
function ProtectedShell() {
  const { status, error, reload, totpEnrollmentRequired, user } = useSession()
  const location = useLocation()

  if (status === 'loading') {
    return (
      <div className="content">
        <LoadingState what="sesi konsol" />
      </div>
    )
  }

  if (status === 'unavailable') {
    return (
      <div className="content">
        <ErrorState
          title="Gagal memeriksa sesi"
          error={error}
          onRetry={reload}
          hint="Konsol perlu memastikan sesi masih berlaku sebelum menampilkan data. Setelah server admin terjangkau, tekan Coba lagi."
        />
      </div>
    )
  }

  if (status === 'anonymous') {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
  }

  if (totpEnrollmentRequired) return <div className="content"><SecurityPage mandatory /></div>
  if (user?.merchant_scope_restricted && ['/clients', '/credentials', '/audit-logs'].some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))) {
    return <div className="content"><h1>Akses dibatasi merchant</h1><p>Halaman ini memuat pengelolaan atau audit global dan tidak tersedia untuk akun dengan cakupan merchant terbatas.</p><a className="btn" href="/">Kembali ke dashboard</a></div>
  }
  return <AppShell key={JSON.stringify([user?.id, user?.role, user?.merchant_scope_restricted, user?.merchant_ids])} />
}

/** Rute yang hanya untuk admin, misalnya pengelolaan pengguna. */
function AdminOnly({ element }: { element: React.ReactNode }) {
  const { role } = useSession()
  if (role !== 'admin') {
    return (
      <div className="state">
        <p className="state__title">Halaman ini hanya untuk admin</p>
        <p className="state__body">
          Akun dengan peran viewer tidak memiliki akses ke pengelolaan pengguna. Menu
          Pengguna juga disembunyikan untuk peran ini.
        </p>
      </div>
    )
  }
  return <>{element}</>
}
